import React, { useEffect, useState, useRef } from "react";
import PropTypes from "kf-proptypes/widgets";
import { Modal } from "antd";
import { createRoot } from "react-dom/client";
import { t } from "@lingui/macro";
import { QueryClientProvider } from "@tanstack/react-query";

import { I18nProvider } from "base/i18n";
import { queryClient } from "base/query";

import {
  KfIcon,
  KF_ICONS,
  ICON_SIZE,
  ICON_STROKE
} from "@widgets/common/components/primitives/kf_icon/index.jsx";
import { ConfigProvider } from "../../layouts/configprovider.jsx";
import { Spinner } from "../spinner/index.jsx";
import { Button, BUTTON_TYPES } from "../../inputs/button/index.jsx";

import "antd/es/modal/style/css";
import styles from "./alert.css";

function createModal(options) {
  const div = document.createElement("div");
  document.body.appendChild(div);

  const root = createRoot(div);
  function destroy() {
    if (root.unmount() && div.parentNode) {
      div.parentNode.removeChild(div);
    }
  }
  root.render(
    <I18nProvider>
      <ConfigProvider direction={window.isRTL ? "rtl" : "ltr"}>
        <QueryClientProvider client={queryClient}>
          <AlertModal {...options} onDestroy={destroy} />
        </QueryClientProvider>
      </ConfigProvider>
    </I18nProvider>
  );

  return { destroy };
}

AlertModal.propTypes = {
  /**
   * waits untill promise returned by `waitFor` function to settled and then
   * shows the popup content
   */
  waitFor: PropTypes.func,
  /** Title for the popup */
  title: PropTypes.string,
  /** Content of the popup */
  content: PropTypes.string.isRequired,
  /** Custom footer render */
  footer: PropTypes.func,
  /** Customise the main button text */
  okText: PropTypes.string,
  /** Cancel button text */
  cancelText: PropTypes.string,
  /** Fires the callback when Ok button gets clicked*/
  onOK: PropTypes.func,
  /** Callback for when closing the alert */
  onCancel: PropTypes.func,
  /** Change the type of the button */
  okButtonType: PropTypes.string,
  /** decides whether to show the cancel button in the alert */
  showCancel: PropTypes.bool,
  /** calls when popup gets closed */
  onDestroy: PropTypes.func,
  /** Toggles the top right close button when setting true */
  closable: PropTypes.bool,
  /** Setting `true` will make the popup closeable when clicking on the overlay */
  closeOnOutsideClick: PropTypes.bool,
  /** setting `true` will close popup when clicking `Esc` key */
  closeOnEscape: PropTypes.bool,
  /**
   * Change container of the modal. Default to document.body
   */
  getContainer: PropTypes.func
};

AlertModal.defaultProps = {
  closable: true,
  closeOnOutsideClick: true,
  closeOnEscape: true
};

export function AlertModal(props) {
  const waitFor = props.waitFor;
  const [isLoading, setIsLoading] = useState(!!props.waitFor);
  const title = useRef(props.title);
  const content = useRef(props.content);
  const footer = useRef(props.footer);
  const okText = useRef(props.okText);
  const cancelText = useRef(props.cancelText);
  const onOK = useRef(props.onOK);
  const onCancel = useRef(props.onCancel);
  const okButtonType = useRef(props.okButtonType || BUTTON_TYPES.PRIMARY);
  const showCancel = useRef(props.showCancel);
  const onDestroy = props.onDestroy;
  const getContainer = props.getContainer;

  useEffect(
    function waitForEffect() {
      if (waitFor) {
        setIsLoading(true);
        waitFor().then((resp) => {
          title.current = resp.title;
          content.current = resp.content;
          footer.current = resp.footer;
          okText.current = resp.okText;
          cancelText.current = resp.cancelText;
          onOK.current = resp.onOK;
          onCancel.current = resp.onCancel;
          okButtonType.current = resp.okButtonType || BUTTON_TYPES.PRIMARY;
          showCancel.current = resp.showCancel;
          setIsLoading(false);
        });
      }
    },
    [waitFor]
  );

  return (
    <Modal
      width={`50rem`}
      wrapClassName={"ant-alert-wrap"}
      maskStyle={{ zIndex: 9999 }}
      title={<span className={`h5600 ${styles.title}`}>{title.current}</span>}
      visible={true}
      className={styles.alert}
      onCancel={() => {
        onDestroy && onDestroy();
        onCancel.current && onCancel.current();
      }}
      closable={props.closable}
      maskClosable={props.closeOnOutsideClick}
      keyboard={props.closeOnEscape}
      centered={true}
      footer={
        (footer.current && footer.current({ destroy: onDestroy })) || (
          <div className={styles.footer}>
            <div className={styles.spacer} />
            <div className={styles.actions}>
              {showCancel.current && (
                <Button
                  type={BUTTON_TYPES.SECONDARY}
                  onClick={() => {
                    onDestroy && onDestroy();
                    onCancel.current && onCancel.current();
                  }}
                >
                  {cancelText.current || t`Cancel`}
                </Button>
              )}
              {onOK && (
                <Button
                  type={okButtonType.current}
                  onClick={() => {
                    onDestroy && onDestroy();
                    onOK.current && onOK.current();
                  }}
                  autoFocus={true}
                >
                  {okText.current || t`OK`}
                </Button>
              )}
            </div>
          </div>
        )
      }
      closeIcon={
        <KfIcon
          icon={KF_ICONS.CLOSE}
          size={ICON_SIZE.SIZE_12}
          stroke={ICON_STROKE.REGULAR}
        />
      }
      getContainer={getContainer}
    >
      <span className={`smallplus ${styles.content}`}>
        {isLoading ? (
          <div className={styles.loaderContainer}>
            <Spinner />
          </div>
        ) : (
          content.current
        )}
      </span>
    </Modal>
  );
}

export function AlertFunctions() {
  return {
    showAlert: (options) => {
      const {
        title,
        content,
        okText,
        onOK,
        closable = true,
        closeOnOutsideClick = true,
        closeOnEscape = true,
        footer = null,
        okButtonType = BUTTON_TYPES.PRIMARY,
        getContainer
      } = options;
      return createModal({
        title: title,
        okText: okText,
        onOK,
        content,
        isOpen: true,
        closable,
        closeOnOutsideClick,
        closeOnEscape,
        footer,
        okButtonType,
        getContainer
      });
    },
    showConfirm: (options) => {
      const {
        title,
        content,
        okText,
        onOK,
        cancelText,
        onCancel,
        okButtonType = BUTTON_TYPES.PRIMARY,
        waitFor,
        footer = null,
        showCancel = true,
        closable = true,
        closeOnOutsideClick = true,
        closeOnEscape = true,
        getContainer
      } = options;
      return createModal({
        title,
        okText,
        onOK,
        cancelText,
        onCancel,
        showCancel,
        content,
        okButtonType,
        footer,
        waitFor,
        closable,
        closeOnOutsideClick,
        closeOnEscape,
        getContainer
      });
    }
  };
}

export const Alert = AlertFunctions();
